import React, { useMemo } from 'react';
import { Sidebar, Progress } from 'flowbite-react';
import Select from 'react-select';

const QuranSidebar = ({
  isDarkMode,
  isMobile,
  sidebarOpen = false,
  customStyles = {},
  surahOptions = [],
  handleSurahChange = () => {},
  selectedSurah = null,
  reciterOptions = [],
  handleReciterChange = () => {},
  selectedReciter = null,
  languageOptions = [],
  handleLanguageChange = () => {},
  selectedLanguage = null,
  translationOptions = [],
  handleTranslationChange = () => {},
  selectedTranslation = null,
  isReadingMode = true,
  setIsReadingMode = () => {},
  showTransliteration = false,
  handleTransliterationToggle = () => {},
  memorizedPercentage = 0,
  memorizedCount = 0,
  totalVerses = 0,
  displayMode = 'with-translation',
  setDisplayMode = () => {},
  // For compatibility with the existing implementation
  isOpen,
  onClose,
  surahs,
  onSurahSelect,
}) => {
  // Create default styles if not provided
  const defaultStyles = useMemo(() => ({
    control: (provided, state) => ({
      ...provided,
      backgroundColor: isDarkMode ? '#111111' : 'white',
      borderColor: state.isFocused ? '#10B981' : isDarkMode ? '#1A1A1A' : '#D1D5DB',
      boxShadow: state.isFocused ? '0 0 0 1px #10B981' : null,
      color: isDarkMode ? 'white' : 'black',
      '&:hover': {
        borderColor: '#10B981'
      }
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected 
        ? '#10B981' 
        : state.isFocused 
          ? isDarkMode ? '#1A1A1A' : '#E5E7EB'
          : isDarkMode ? '#111111' : null,
      color: state.isSelected 
        ? 'white' 
        : isDarkMode 
          ? 'white' 
          : 'black',
      padding: '8px 12px',
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: isDarkMode ? '#0A0A0A' : 'white',
      borderColor: isDarkMode ? '#1A1A1A' : '#D1D5DB',
      borderWidth: '1px',
      borderRadius: '0.75rem',
      padding: '0.5rem',
      boxShadow: isDarkMode ? '0 10px 15px -3px rgba(0, 0, 0, 0.3)' : '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    }),
    menuList: (provided) => ({
      ...provided,
      backgroundColor: isDarkMode ? '#0A0A0A' : 'white',
      borderRadius: '0.5rem',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: isDarkMode ? 'white' : 'black',
    }),
  }), [isDarkMode]);

  // Use provided styles or default styles
  const styles = Object.keys(customStyles).length > 0 ? customStyles : defaultStyles;

  // Use the correct sidebarOpen prop
  const effectiveSidebarOpen = sidebarOpen !== undefined ? sidebarOpen : isOpen;

  return (
    <div className={`fixed inset-y-0 left-0 z-30 ${
      isMobile ? 'w-full' : 'quran-sidebar w-[350px]'
    } ${
      isDarkMode ? 'bg-[#111111]' : 'bg-white'
    } transition-transform duration-300 ease-in-out transform ${
      effectiveSidebarOpen ? 'translate-x-0' : '-translate-x-full'
    } lg:translate-x-0`}>
      <div className={`h-full flex flex-col ${
        isMobile ? 'pb-40 pt-[env(safe-area-inset-top)]' : 'pb-20 pt-16'
      } overflow-y-auto ${
        isDarkMode ? 'bg-[#111111] border-r border-gray-800' : 'bg-white border-r border-gray-200'
      }`}>
        <Sidebar aria-label="Quran Explorer Sidebar" className={`quran-sidebar ${
          isDarkMode ? 'bg-[#111111]' : 'bg-white'
        }`}>
          <Sidebar.Items className={`${isDarkMode ? 'bg-[#111111]' : 'bg-white'} ${
            isMobile ? 'w-full px-6' : 'max-w-sm mx-auto w-full px-4'
          }`}>
            <Sidebar.ItemGroup className="border-none space-y-4">
              <div className="p-2">
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>Surah</label>
                {Array.isArray(surahOptions) && surahOptions.length > 0 ? (
                  <Select 
                    options={surahOptions}
                    onChange={handleSurahChange || onSurahSelect}
                    value={(surahOptions || []).find(option => option.value === selectedSurah)}
                    styles={styles}
                    placeholder="Select Surah"
                  />
                ) : (
                  <div className={`text-sm py-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Loading surahs...
                  </div>
                )}
              </div>
              <div className="p-2">
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>Reciter</label>
                {Array.isArray(reciterOptions) && reciterOptions.length > 0 ? (
                  <Select 
                    options={reciterOptions}
                    onChange={handleReciterChange}
                    value={(reciterOptions || []).find(option => option.value === selectedReciter)}
                    styles={styles}
                    placeholder="Select Reciter"
                  />
                ) : (
                  <div className={`text-sm py-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Loading reciters...
                  </div>
                )}
              </div>
              <div className="p-2">
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>Language</label>
                {Array.isArray(languageOptions) && languageOptions.length > 0 ? (
                  <Select 
                    options={languageOptions}
                    onChange={handleLanguageChange}
                    value={(languageOptions || []).find(option => option.value === selectedLanguage)}
                    styles={styles}
                    placeholder="Select Language"
                  />
                ) : (
                  <div className={`text-sm py-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Loading languages...
                  </div>
                )}
              </div>
              <div className="p-2">
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>Translation</label>
                {Array.isArray(translationOptions) && translationOptions.length > 0 ? (
                  <Select 
                    options={translationOptions}
                    onChange={handleTranslationChange}
                    value={(translationOptions || []).find(option => option.value === selectedTranslation)}
                    styles={styles}
                    placeholder="Select Translation"
                  />
                ) : (
                  <div className={`text-sm py-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Loading translations...
                  </div>
                )}
              </div>
              <div className="p-2">
                <div className={`flex items-center justify-between py-3 rounded-lg`}>
                  <span className={`text-sm font-medium ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>Arabic Only</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={displayMode === 'arabic-only'}
                      onChange={() => setDisplayMode(displayMode === 'arabic-only' ? 'with-translation' : 'arabic-only')}
                      className="sr-only peer"
                    />
                    <div className={`w-11 h-6 ${
                      isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
                    } peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 dark:peer-focus:ring-green-800 rounded-full peer ${
                      isDarkMode ? 'dark:border-gray-800' : 'dark:border-gray-600'
                    } peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600`}></div>
                  </label>
                </div>
              </div>
              <div className="p-2">
                <div className={`flex items-center justify-between py-3 rounded-lg`}>
                  <span className={`text-sm font-medium ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>Show Transliteration</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showTransliteration}
                      onChange={handleTransliterationToggle}
                      className="sr-only peer"
                    />
                    <div className={`w-11 h-6 ${
                      isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
                    } peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 dark:peer-focus:ring-green-800 rounded-full peer ${
                      isDarkMode ? 'dark:border-gray-800' : 'dark:border-gray-600'
                    } peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600`}></div>
                  </label>
                </div>
              </div>
              <div className="p-2">
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>Memorization Progress</label>
                <Progress 
                  progress={memorizedPercentage} 
                  size="lg" 
                  color="green"
                  className={`${isDarkMode ? 'bg-[#1A1A1A]' : 'bg-white bg-opacity-20'}`}
                />
                <p className={`text-center mt-2 text-sm ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  {memorizedCount}/{totalVerses} verses ({memorizedPercentage.toFixed(2)}%)
                </p>
              </div>
            </Sidebar.ItemGroup>
          </Sidebar.Items>
        </Sidebar>
      </div>
    </div>
  );
};

export default QuranSidebar;
